<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import {
  AlertOctagon,
  Check,
  CircleCheckBig,
  CheckSquare2,
  ChevronDown,
  ChevronRight,
  Circle,
  Diamond,
  FileText,
  EyeOff,
  MessageSquareText,
  MoreHorizontal,
  Plus,
  Rows3,
  ListChecks,
  ClipboardList,
} from "lucide-vue-next";
import PersonAvatar from "@/components/people/PersonAvatar.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import { shortDate } from "@/utils/dates";
import {
  calculateTaskProgress,
  descendantTasks,
  visibleTaskHierarchy,
} from "@/utils/progress";
import { detailSectionForStatusChange } from "@/utils/task-status";
import type {
  Participant,
  ProjectSection,
  ProjectTask,
  TaskStatus,
} from "@/types/models";

const props = defineProps<{
  tasks: ProjectTask[];
  sections: ProjectSection[];
  hideEmptySections?: boolean;
  progressTasks?: ProjectTask[];
  selectionMode?: boolean;
  selectedTaskIds?: string[];
  selectionLimit?: number;
  expandedSectionIds?: string[];
}>();
const emit = defineEmits<{
  openTask: [id: string, section?: string, status?: TaskStatus];
  addItem: [sectionId: string];
  toggleTaskSelection: [id: string];
  selectVisibleTasks: [ids: string[], selected: boolean];
  "update:expandedSectionIds": [ids: string[]];
}>();
const workspace = useWorkspaceStore();
const localExpandedSectionIds = ref<string[]>([]);
const menuTask = ref<ProjectTask | null>(null);
const menuPosition = ref({ top: 0, left: 0 });
const menu = ref<HTMLElement | null>(null);
const menuSaving = ref(false);
const menuError = ref("");
let menuOpener: HTMLElement | null = null;
const ownerMenuTask = ref<ProjectTask | null>(null);
const ownerMenuPosition = ref({ top: 0, left: 0 });
const ownerMenu = ref<HTMLElement | null>(null);
const ownerMenuSaving = ref(false);
const ownerMenuError = ref("");
let ownerMenuOpener: HTMLElement | null = null;
const statusOptions: TaskStatus[] = [
  "Not started",
  "In progress",
  "Blocked",
  "In review",
  "Complete",
  "Not applicable",
  "Cancelled",
];
const maximumSectionAvatars = 6;
const selectableTaskIds = computed(() => props.tasks.map((task) => task.id));
const selectedTaskIdSet = computed(() => new Set(props.selectedTaskIds || []));
const selectionLimit = computed(() => props.selectionLimit ?? 50);
const selectionLimitReached = computed(
  () => selectedTaskIdSet.value.size >= selectionLimit.value,
);
const allVisibleSelected = computed(
  () =>
    selectableTaskIds.value.length > 0 &&
    selectableTaskIds.value.every((id) => selectedTaskIdSet.value.has(id)),
);
const selectVisibleWouldExceedLimit = computed(() => {
  const missing = selectableTaskIds.value.filter(
    (id) => !selectedTaskIdSet.value.has(id),
  ).length;
  return (
    !allVisibleSelected.value &&
    selectedTaskIdSet.value.size + missing > selectionLimit.value
  );
});

function assigneesFor(tasks: readonly ProjectTask[]) {
  const assignees = new Map<
    string,
    { id: string; name: string; initials: string; photoUrl?: string }
  >();
  for (const task of tasks) {
    if (
      task.type === "summary" ||
      !task.ownerName ||
      task.ownerName === "Unassigned" ||
      task.ownerInitials === "—"
    )
      continue;
    const id = task.ownerId
      ? `${task.ownerId}:${task.ownerName}`
      : task.ownerName;
    if (!assignees.has(id))
      assignees.set(id, {
        id,
        name: task.ownerName,
        initials: task.ownerInitials,
        photoUrl: task.ownerPhotoUrl,
      });
  }
  return [...assignees.values()];
}

const grouped = computed(() => {
  const progressTasks = props.progressTasks || props.tasks;
  const visibleMilestones = props.tasks
    .filter((task) => task.type === "milestone")
    .sort((left, right) =>
      (left.dueDate || "9999-12-31").localeCompare(
        right.dueDate || "9999-12-31",
      ),
    );
  const allMilestones = progressTasks.filter(
    (task) => task.type === "milestone",
  );
  const milestoneAssignees = assigneesFor(allMilestones);
  const milestoneGroup = {
    section: {
      id: "milestones-aggregate",
      name: "Milestones",
      progress: calculateTaskProgress(allMilestones).progress,
    },
    tasks: visibleMilestones,
    assignees: milestoneAssignees.slice(0, maximumSectionAvatars),
    remainingAssigneeCount: Math.max(
      0,
      milestoneAssignees.length - maximumSectionAvatars,
    ),
    remainingAssigneeNames: milestoneAssignees
      .slice(maximumSectionAvatars)
      .map((assignee) => assignee.name)
      .join(", "),
    progress: calculateTaskProgress(allMilestones).progress,
    aggregate: true,
  };
  const groups = props.sections.map((section) => {
    const visibleTasks = props.tasks.filter(
      (task) => task.sectionId === section.id,
    );
    const allSectionTasks = progressTasks.filter(
      (task) => task.sectionId === section.id,
    );
    const tasks = visibleTaskHierarchy(visibleTasks, allSectionTasks);
    const assignees = assigneesFor(allSectionTasks);
    return {
      section,
      tasks,
      assignees: assignees.slice(0, maximumSectionAvatars),
      remainingAssigneeCount: Math.max(
        0,
        assignees.length - maximumSectionAvatars,
      ),
      remainingAssigneeNames: assignees
        .slice(maximumSectionAvatars)
        .map((assignee) => assignee.name)
        .join(", "),
      progress: calculateTaskProgress(allSectionTasks).progress,
      aggregate: false,
    };
  });
  const visibleGroups = props.hideEmptySections
    ? groups.filter((group) => group.tasks.length)
    : groups;
  return props.hideEmptySections && !milestoneGroup.tasks.length
    ? visibleGroups
    : [milestoneGroup, ...visibleGroups];
});
const expandedSectionIdSet = computed(
  () => new Set(props.expandedSectionIds ?? localExpandedSectionIds.value),
);
const visibleSectionIds = computed(() =>
  grouped.value.map((group) => group.section.id),
);
const allSectionsExpanded = computed(
  () =>
    visibleSectionIds.value.length > 0 &&
    visibleSectionIds.value.every((id) => expandedSectionIdSet.value.has(id)),
);
const allSectionsCollapsed = computed(() =>
  visibleSectionIds.value.every((id) => !expandedSectionIdSet.value.has(id)),
);

watch(visibleSectionIds, (ids, previousIds) => {
  if (
    !previousIds.length ||
    !previousIds.every((id) => expandedSectionIdSet.value.has(id))
  )
    return;
  const next = new Set(expandedSectionIdSet.value);
  ids.forEach((id) => next.add(id));
  if (next.size !== expandedSectionIdSet.value.size)
    setExpandedSectionIds([...next]);
});

const collectionCandidates = computed(() => {
  const task = menuTask.value;
  if (!task) return [];
  return (props.progressTasks || props.tasks).filter(
    (candidate) =>
      candidate.type === "summary" &&
      candidate.sectionId === task.sectionId &&
      candidate.visibility === task.visibility,
  );
});

const eligibleOwners = computed(() => {
  const task = ownerMenuTask.value;
  if (!task) return [];
  return (workspace.active?.participants || [])
    .filter(
      (person) =>
        person.state !== "pending" &&
        (task.visibility === "Shared" || person.kind === "internal"),
    )
    .sort((left, right) => left.name.localeCompare(right.name));
});

function taskCollectionProgress(task: ProjectTask): number {
  return calculateTaskProgress(
    descendantTasks(props.progressTasks || props.tasks, task.id),
  ).progress;
}

function itemTypeLabel(task: ProjectTask): string {
  if (task.type === "milestone") return "Milestone";
  if (task.type === "summary") return "Task collection";
  if (task.type === "checklist") return "Checklist";
  if (task.type === "questionnaire") return "Questionnaire";
  return "Task";
}

function responseSummary(task: ProjectTask): string {
  if (task.type === "checklist")
    return `${task.checklist.filter((item) => item.complete).length} of ${task.checklist.length} checked · ${task.progress}%`;
  const required = task.questions.filter(
    (question) => question.required !== false,
  );
  return `${required.filter((question) => question.answer?.trim()).length} of ${required.length} required answered · ${task.progress}%`;
}

function statusUnavailable(task: ProjectTask, status: TaskStatus): boolean {
  if (status !== "Complete") return false;
  if (task.type === "checklist") return true;
  return (
    task.type === "questionnaire" &&
    task.status !== "In review" &&
    task.status !== "Complete"
  );
}

async function changeStatus(task: ProjectTask, event: Event) {
  const select = event.target as HTMLSelectElement;
  const status = select.value as TaskStatus;
  if (status === task.status) return;
  const section = detailSectionForStatusChange(
    task,
    status,
    props.progressTasks || props.tasks,
  );
  if (section) {
    select.value = task.status;
    emit("openTask", task.id, section, status);
    return;
  }
  const updated = await workspace.setTaskStatus(task.id, status);
  if (!updated) select.value = task.status;
}

function setExpandedSectionIds(ids: string[]) {
  const next = [...new Set(ids)];
  if (props.expandedSectionIds === undefined)
    localExpandedSectionIds.value = next;
  emit("update:expandedSectionIds", next);
}

function toggle(id: string) {
  const next = new Set(expandedSectionIdSet.value);
  next.has(id) ? next.delete(id) : next.add(id);
  setExpandedSectionIds([...next]);
}

function expandAll() {
  setExpandedSectionIds(visibleSectionIds.value);
}

function collapseAll() {
  setExpandedSectionIds([]);
}

async function openMenu(event: MouseEvent, task: ProjectTask) {
  const button = event.currentTarget as HTMLElement;
  ownerMenuTask.value = null;
  ownerMenuError.value = "";
  menuOpener = button;
  const rect = button.getBoundingClientRect();
  menuTask.value = task;
  menuSaving.value = false;
  menuError.value = "";
  menuPosition.value = {
    top: Math.max(8, rect.bottom + 5),
    left: Math.max(8, Math.min(rect.right - 250, window.innerWidth - 258)),
  };
  await nextTick();
  if (menu.value) {
    menuPosition.value.top = Math.max(
      8,
      Math.min(
        rect.bottom + 5,
        window.innerHeight - menu.value.offsetHeight - 8,
      ),
    );
  }
  menu.value?.querySelector<HTMLButtonElement>("button")?.focus();
}

async function openOwnerMenu(event: MouseEvent, task: ProjectTask) {
  if (!workspace.canEditPlan || task.type === "summary") return;
  const button = event.currentTarget as HTMLElement;
  menuTask.value = null;
  menuError.value = "";
  ownerMenuOpener = button;
  const rect = button.getBoundingClientRect();
  ownerMenuTask.value = task;
  ownerMenuSaving.value = false;
  ownerMenuError.value = "";
  ownerMenuPosition.value = {
    top: Math.max(8, rect.bottom + 5),
    left: Math.max(8, Math.min(rect.right - 286, window.innerWidth - 294)),
  };
  await nextTick();
  if (ownerMenu.value) {
    ownerMenuPosition.value.top = Math.max(
      8,
      Math.min(
        rect.bottom + 5,
        window.innerHeight - ownerMenu.value.offsetHeight - 8,
      ),
    );
  }
  ownerMenu.value
    ?.querySelector<HTMLButtonElement>('button[role="menuitemradio"]')
    ?.focus();
}

async function closeMenu(restoreFocus = true) {
  menuTask.value = null;
  menuSaving.value = false;
  menuError.value = "";
  if (restoreFocus) {
    await nextTick();
    menuOpener?.focus();
  }
}

async function closeOwnerMenu(restoreFocus = true) {
  ownerMenuTask.value = null;
  ownerMenuSaving.value = false;
  ownerMenuError.value = "";
  if (restoreFocus) {
    await nextTick();
    ownerMenuOpener?.focus();
  }
}

function handleMenuNavigation(event: KeyboardEvent) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  const items = [
    ...(event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(
      '[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)',
    ),
  ];
  if (!items.length) return;
  event.preventDefault();
  const currentIndex = items.indexOf(
    document.activeElement as HTMLButtonElement,
  );
  const nextIndex =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? items.length - 1
        : event.key === "ArrowDown"
          ? (currentIndex + 1 + items.length) % items.length
          : (currentIndex - 1 + items.length) % items.length;
  items[nextIndex]?.focus();
}

async function changeOwner(person?: Participant) {
  const task = ownerMenuTask.value;
  if (!task || ownerMenuSaving.value) return;
  const nextOwnerId = person?.id || "";
  if ((task.ownerId || "") === nextOwnerId) {
    await closeOwnerMenu();
    return;
  }
  ownerMenuSaving.value = true;
  ownerMenuError.value = "";
  try {
    await workspace.updateTask(
      task.id,
      person
        ? {
            ownerId: person.id,
            ownerName: person.name,
            ownerInitials: person.initials,
            ownerPhotoUrl: person.photoUrl,
            ownerOrganization: person.organization,
          }
        : {
            ownerId: "",
            ownerName: "Unassigned",
            ownerInitials: "—",
            ownerPhotoUrl: undefined,
            ownerOrganization: "No organization",
          },
      task.version,
    );
    await closeOwnerMenu();
  } catch (error) {
    ownerMenuError.value =
      error instanceof Error
        ? `${error.message} The owner was not changed.`
        : "We could not change the owner. The owner was not changed. Try again.";
  } finally {
    ownerMenuSaving.value = false;
  }
}

async function moveToCollection(parentId: string) {
  const task = menuTask.value;
  if (!task || menuSaving.value || (task.parentId || "") === parentId) return;
  menuSaving.value = true;
  menuError.value = "";
  try {
    await workspace.updateTask(task.id, { parentId }, task.version);
    await closeMenu();
  } catch (error) {
    menuError.value =
      error instanceof Error
        ? `${error.message} The task stayed in its current collection.`
        : "We could not change this task collection. The task stayed where it was. Try again.";
  } finally {
    menuSaving.value = false;
  }
}

async function choose(action: "open" | "dependency" | "complete" | "na") {
  const task = menuTask.value;
  menuTask.value = null;
  if (!task) return;
  if (action === "open") emit("openTask", task.id, "overview");
  if (action === "dependency") emit("openTask", task.id, "schedule");
  if (action === "complete") workspace.setTaskStatus(task.id, "Complete");
  if (action === "na") workspace.setTaskStatus(task.id, "Not applicable");
  if (action === "complete" || action === "na") {
    await nextTick();
    menuOpener?.focus();
  }
}
</script>

<template>
  <div
    class="plan-list"
    :class="{
      'plan-list--client': !workspace.isInternal,
      'plan-list--selection': selectionMode,
    }"
    role="region"
    aria-label="Project plan list"
    tabindex="0"
  >
    <div
      class="section-controls"
      role="group"
      aria-label="Section display controls"
    >
      <button
        class="section-control"
        type="button"
        :disabled="allSectionsExpanded || !visibleSectionIds.length"
        @click="expandAll"
      >
        Expand all
      </button>
      <button
        class="section-control"
        type="button"
        :disabled="allSectionsCollapsed || !visibleSectionIds.length"
        @click="collapseAll"
      >
        Collapse all
      </button>
    </div>
    <div class="plan-columns" :aria-hidden="!selectionMode">
      <label
        v-if="selectionMode"
        class="selection-column selection-column--all"
      >
        <input
          type="checkbox"
          :checked="allVisibleSelected"
          :disabled="selectVisibleWouldExceedLimit"
          :aria-label="
            allVisibleSelected
              ? 'Clear all visible task selections'
              : 'Select all visible tasks'
          "
          @change="
            emit('selectVisibleTasks', selectableTaskIds, !allVisibleSelected)
          "
        />
      </label>
      <span>Work</span><span>Owner / progress</span><span>Start</span
      ><span>Due</span><span>Status</span><span>Health</span
      ><span>Activity</span>
    </div>
    <section
      v-for="group in grouped"
      :key="group.section.id"
      class="plan-section"
    >
      <header class="section-row">
        <button
          class="section-toggle"
          type="button"
          :aria-expanded="expandedSectionIdSet.has(group.section.id)"
          @click="toggle(group.section.id)"
        >
          <ChevronRight
            v-if="!expandedSectionIdSet.has(group.section.id)"
            :size="17"
          /><ChevronDown v-else :size="17" />
          <span class="section-name">{{ group.section.name }}</span>
          <span class="section-meta">{{ group.tasks.length }} items</span>
          <span
            v-if="group.assignees.length"
            class="section-assignees"
            role="list"
            :aria-label="`People assigned in ${group.section.name}`"
          >
            <span
              v-for="assignee in group.assignees"
              :key="assignee.id"
              role="listitem"
            >
              <PersonAvatar
                :name="assignee.name"
                :initials="assignee.initials"
                :photo-url="assignee.photoUrl"
              />
            </span>
            <span
              v-if="group.remainingAssigneeCount"
              class="section-assignees__more"
              role="listitem"
              :title="group.remainingAssigneeNames"
              :aria-label="`${group.remainingAssigneeCount} more people: ${group.remainingAssigneeNames}`"
            >
              +{{ group.remainingAssigneeCount }}
            </span>
          </span>
          <span class="section-progress"
            ><span class="progress-track"
              ><span :style="{ width: `${group.progress}%` }"></span></span
            ><strong class="tabular">{{ group.progress }}%</strong></span
          >
          <span
            v-if="group.tasks.some((task) => task.status === 'Blocked')"
            class="section-blocked"
            ><AlertOctagon :size="14" /> Blocked work</span
          >
        </button>
        <button
          v-if="workspace.isInternal && !group.aggregate"
          class="section-add"
          type="button"
          :aria-label="`Add work to ${group.section.name}`"
          @click="emit('addItem', group.section.id)"
        >
          <Plus :size="14" /> Add work
        </button>
      </header>
      <div v-if="expandedSectionIdSet.has(group.section.id)">
        <div
          v-for="task in group.tasks"
          :key="task.id"
          class="task-row"
          :class="[
            `task-row--${task.type}`,
            {
              'task-row--blocked': task.status === 'Blocked',
              'task-row--complete': task.status === 'Complete',
              'task-row--internal': task.visibility === 'Internal',
              'task-row--not-applicable': task.status === 'Not applicable',
            },
          ]"
        >
          <label v-if="selectionMode" class="selection-column">
            <input
              type="checkbox"
              :checked="selectedTaskIdSet.has(task.id)"
              :disabled="
                selectionLimitReached && !selectedTaskIdSet.has(task.id)
              "
              :aria-label="
                selectionLimitReached && !selectedTaskIdSet.has(task.id)
                  ? 'The ' +
                    selectionLimit +
                    '-task selection maximum is reached'
                  : 'Select ' +
                    task.title +
                    (task.type === 'summary'
                      ? '; task collections can only be deleted in bulk'
                      : '')
              "
              @change="emit('toggleTaskSelection', task.id)"
            />
          </label>
          <button
            class="task-cell task-title"
            type="button"
            :style="{ '--level': task.level }"
            :aria-label="task.type === 'task' ? task.title : undefined"
            @click="emit('openTask', task.id)"
          >
            <span class="task-symbol" :class="`task-symbol--${task.type}`"
              ><Diamond
                v-if="task.type === 'milestone'"
                :size="14"
                fill="currentColor" /><Rows3
                v-else-if="task.type === 'summary'"
                :size="14" /><ListChecks
                v-else-if="task.type === 'checklist'"
                :size="14" /><ClipboardList
                v-else-if="task.type === 'questionnaire'"
                :size="14" /><Circle v-else :size="9" fill="currentColor"
            /></span>
            <span class="task-title__copy"
              ><span class="task-title__line"
                ><strong>{{ task.title }}</strong
                ><CircleCheckBig
                  v-if="task.status === 'Complete'"
                  :size="16"
                  :stroke-width="2.5"
                  class="completion-marker"
                  aria-hidden="true" />
                ><EyeOff
                  v-if="workspace.isInternal && task.visibility === 'Internal'"
                  :size="13"
                  class="internal-marker"
                  aria-label="Internal — visible only to VIPdesk project participants"
                  title="Internal — visible only to VIPdesk project participants" /></span
              ><small v-if="task.type !== 'task'" class="task-type-label">
                {{ itemTypeLabel(task) }} </small
              ><small v-if="['checklist', 'questionnaire'].includes(task.type)"
                ><CheckSquare2 :size="12" /> {{ responseSummary(task) }}</small
              ></span
            >
          </button>
          <span
            v-if="task.type === 'summary'"
            class="task-cell collection-progress-cell"
          >
            <span
              class="collection-progress-track"
              role="progressbar"
              :aria-label="`Calculated progress for ${task.title}`"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="taskCollectionProgress(task)"
            >
              <span
                :style="{ width: `${taskCollectionProgress(task)}%` }"
              ></span>
            </span>
            <strong class="tabular">{{ taskCollectionProgress(task) }}%</strong>
          </span>
          <component
            :is="workspace.canEditPlan ? 'button' : 'span'"
            v-else
            class="task-cell owner-cell"
            :class="{ 'owner-cell--interactive': workspace.canEditPlan }"
            :type="workspace.canEditPlan ? 'button' : undefined"
            :aria-label="
              workspace.canEditPlan
                ? `Change owner for ${task.title}. Current owner: ${task.ownerName}`
                : undefined
            "
            :aria-haspopup="workspace.canEditPlan ? 'menu' : undefined"
            :aria-expanded="
              workspace.canEditPlan ? ownerMenuTask?.id === task.id : undefined
            "
            :title="workspace.canEditPlan ? 'Change owner' : undefined"
            @click.stop="openOwnerMenu($event, task)"
            ><PersonAvatar
              :name="task.ownerName"
              :initials="task.ownerInitials"
              :photo-url="task.ownerPhotoUrl"
            />
            <span class="truncate"
              ><strong>{{ task.ownerName }}</strong
              ><small>{{ task.ownerOrganization }}</small></span
            ></component
          >
          <time class="task-cell date-cell tabular">{{
            shortDate(task.startDate)
          }}</time>
          <time
            class="task-cell date-cell tabular"
            :class="{ 'due-risk': task.health === 'Overdue' }"
            >{{ shortDate(task.dueDate) }}</time
          >
          <span class="task-cell status-cell" @click.stop
            ><span
              v-if="task.type === 'summary'"
              class="calculated-status"
              :class="{
                'calculated-status--complete': task.status === 'Complete',
              }"
            >
              {{ task.status }}
            </span>
            <select
              v-else
              class="status-select"
              :class="{
                'status-select--complete': task.status === 'Complete',
              }"
              :value="task.status"
              :aria-label="`Status for ${task.title}`"
              :disabled="!workspace.canEditPlan"
              :title="
                !workspace.canEditPlan
                  ? 'Your project role cannot change task status.'
                  : undefined
              "
              @change="changeStatus(task, $event)"
            >
              <option
                v-for="status in statusOptions"
                :key="status"
                :disabled="statusUnavailable(task, status)"
              >
                {{ status }}
              </option>
            </select></span
          >
          <span class="task-cell health-cell"
            ><span
              class="health"
              :class="`health--${task.health.toLowerCase().replace(' ', '-')}`"
              ><span></span>{{ task.health }}</span
            ></span
          >
          <span class="task-cell activity-cell"
            ><button
              v-if="task.commentCount"
              class="activity-link"
              type="button"
              :aria-label="`${task.commentCount} comment${task.commentCount === 1 ? '' : 's'} on ${task.title}. Open comments`"
              title="Open comments"
              @click.stop="emit('openTask', task.id, 'comments')"
            >
              <MessageSquareText :size="14" aria-hidden="true" />{{
                task.commentCount
              }}</button
            ><button
              v-if="task.fileCount"
              class="activity-link"
              type="button"
              :aria-label="`${task.fileCount} file${task.fileCount === 1 ? '' : 's'} on ${task.title}. Open files`"
              title="Open files"
              @click.stop="emit('openTask', task.id, 'files')"
            >
              <FileText :size="14" aria-hidden="true" />{{
                task.fileCount
              }}</button
            ><button
              class="icon-button row-menu"
              type="button"
              :aria-label="`More actions for ${task.title}`"
              aria-haspopup="menu"
              :aria-expanded="menuTask?.id === task.id"
              @click.stop="openMenu($event, task)"
            >
              <MoreHorizontal :size="17" /></button
          ></span>
        </div>
      </div>
    </section>
    <div v-if="!tasks.length" class="empty-plan">
      <Rows3 :size="26" /><strong>No work matches these filters.</strong
      ><span>Clear a filter to see the full project plan.</span>
    </div>
    <Teleport to="body">
      <button
        v-if="menuTask"
        class="menu-dismiss"
        type="button"
        aria-label="Close task actions"
        @click="closeMenu()"
      ></button>
      <div
        v-if="menuTask"
        ref="menu"
        class="task-actions-menu"
        role="menu"
        :aria-label="`Actions for ${menuTask.title}`"
        :style="{
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
        }"
        @keydown.esc="closeMenu()"
        @keydown="handleMenuNavigation"
      >
        <button type="button" role="menuitem" @click="choose('open')">
          Open {{ itemTypeLabel(menuTask).toLowerCase() }} details
        </button>
        <div
          v-if="workspace.canEditPlan && menuTask.type !== 'summary'"
          class="collection-menu-group"
          role="group"
          aria-labelledby="collection-menu-label"
        >
          <p id="collection-menu-label">Task collection</p>
          <button
            type="button"
            role="menuitem"
            :disabled="menuSaving || !menuTask.parentId"
            :aria-label="
              menuTask.parentId
                ? `Remove ${menuTask.title} from its task collection`
                : 'Current task collection: None'
            "
            @click="moveToCollection('')"
          >
            <span>No task collection</span>
            <Check v-if="!menuTask.parentId" :size="15" aria-hidden="true" />
          </button>
          <button
            v-for="collection in collectionCandidates"
            :key="collection.id"
            type="button"
            role="menuitem"
            :disabled="menuSaving || menuTask.parentId === collection.id"
            :aria-label="
              menuTask.parentId === collection.id
                ? `Current task collection: ${collection.title}`
                : `Add ${menuTask.title} to ${collection.title}`
            "
            @click="moveToCollection(collection.id)"
          >
            <span>{{ collection.title }}</span>
            <Check
              v-if="menuTask.parentId === collection.id"
              :size="15"
              aria-hidden="true"
            />
          </button>
          <p v-if="!collectionCandidates.length" class="collection-menu-empty">
            No matching collections. A collection must use this task's section
            and access level.
          </p>
          <small v-else-if="menuSaving">Moving task…</small>
          <small v-else
            >Only matching sections and access levels are listed.</small
          >
          <p v-if="menuError" class="collection-menu-error" role="alert">
            {{ menuError }}
          </p>
        </div>
        <button
          v-if="workspace.canEditPlan && menuTask.type !== 'summary'"
          type="button"
          role="menuitem"
          @click="choose('dependency')"
        >
          Add or review dependencies
        </button>
        <button
          v-if="
            !['summary', 'checklist'].includes(menuTask.type) &&
            (menuTask.type !== 'questionnaire' ||
              (menuTask.status === 'In review' &&
                menuTask.ownerId === workspace.user?.id)) &&
            workspace.canEditPlan
          "
          type="button"
          role="menuitem"
          @click="choose('complete')"
        >
          Mark complete
        </button>
        <button
          v-if="menuTask.type !== 'summary' && workspace.canEditPlan"
          type="button"
          role="menuitem"
          @click="choose('na')"
        >
          Mark not applicable
        </button>
      </div>
      <button
        v-if="ownerMenuTask"
        class="menu-dismiss"
        type="button"
        aria-label="Close owner menu"
        @click="closeOwnerMenu()"
      ></button>
      <div
        v-if="ownerMenuTask"
        ref="ownerMenu"
        class="task-actions-menu owner-menu"
        role="menu"
        :aria-label="`Change owner for ${ownerMenuTask.title}`"
        :style="{
          top: `${ownerMenuPosition.top}px`,
          left: `${ownerMenuPosition.left}px`,
        }"
        @keydown.esc="closeOwnerMenu()"
        @keydown="handleMenuNavigation"
      >
        <p class="owner-menu__title">Change owner</p>
        <button
          type="button"
          role="menuitemradio"
          :aria-checked="!ownerMenuTask.ownerId"
          :aria-label="
            !ownerMenuTask.ownerId
              ? 'Unassigned, current owner'
              : 'Unassigned, remove the current owner'
          "
          :disabled="ownerMenuSaving"
          @click="changeOwner()"
        >
          <PersonAvatar name="Unassigned" initials="—" />
          <span><strong>Unassigned</strong><small>No owner</small></span>
          <Check v-if="!ownerMenuTask.ownerId" :size="15" aria-hidden="true" />
        </button>
        <button
          v-for="person in eligibleOwners"
          :key="person.id"
          type="button"
          role="menuitemradio"
          :aria-checked="ownerMenuTask.ownerId === person.id"
          :aria-label="`${person.name}, ${person.organization}${ownerMenuTask.ownerId === person.id ? ', current owner' : ''}`"
          :disabled="ownerMenuSaving"
          @click="changeOwner(person)"
        >
          <PersonAvatar
            :name="person.name"
            :initials="person.initials"
            :photo-url="person.photoUrl"
          />
          <span class="truncate"
            ><strong>{{ person.name }}</strong
            ><small>{{ person.organization }}</small></span
          >
          <Check
            v-if="ownerMenuTask.ownerId === person.id"
            :size="15"
            aria-hidden="true"
          />
        </button>
        <small v-if="ownerMenuSaving" class="owner-menu__status"
          >Changing owner…</small
        >
        <p v-if="ownerMenuError" class="collection-menu-error" role="alert">
          {{ ownerMenuError }}
        </p>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.plan-list {
  min-width: 980px;
  background: white;
}
.plan-columns,
.task-row {
  display: grid;
  grid-template-columns:
    minmax(340px, 2.2fr) minmax(150px, 0.9fr)
    86px 86px 126px 108px 92px;
}
.plan-list--client .plan-columns,
.plan-list--client .task-row {
  grid-template-columns:
    minmax(340px, 2.2fr) minmax(150px, 0.9fr)
    86px 86px 126px 108px 92px;
}
.plan-list--selection .plan-columns,
.plan-list--selection .task-row {
  grid-template-columns:
    42px minmax(340px, 2.2fr) minmax(150px, 0.9fr)
    86px 86px 126px 108px 92px;
}
.selection-column {
  display: grid;
  place-items: center;
  border-right: 1px solid #e4e9e8;
}
.selection-column input {
  width: 17px;
  height: 17px;
  accent-color: var(--brand-strong);
}
.selection-column--all {
  align-self: stretch;
}
.section-controls {
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-inline: 8px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet);
}
.section-control {
  min-height: 30px;
  padding-inline: 8px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.section-control:hover:not(:disabled) {
  background: var(--sheet-strong);
  color: var(--ink-900);
}
.section-control:disabled {
  color: var(--text-faint);
  cursor: default;
  opacity: 0.62;
}
.plan-columns {
  position: sticky;
  top: 0;
  z-index: 8;
  height: 38px;
  align-items: center;
  border-bottom: 1px solid var(--rule-strong);
  background: #eef2f1;
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.plan-columns span,
.task-cell {
  padding-inline: 11px;
}
.section-row {
  position: sticky;
  top: 38px;
  z-index: 7;
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-block: 1px solid #9eaeaa;
  background: #e8eeed;
  color: var(--ink-900);
}
.section-toggle {
  min-width: 0;
  min-height: 46px;
  display: grid;
  flex: 1;
  grid-template-columns: 22px auto auto auto minmax(160px, 1fr) auto;
  align-items: center;
  gap: 7px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.section-add {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 10px;
  padding: 0 8px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.section-add:hover {
  background: white;
  color: var(--ink-900);
}
.plan-section + .plan-section .section-row {
  border-top: 0;
}
.section-name {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}
.section-meta {
  color: var(--text-faint);
  font-size: 11px;
}
.section-assignees {
  min-width: 0;
  display: flex;
  grid-column: 4;
  align-items: center;
  padding-inline: 3px 8px;
}
.section-assignees > span {
  display: inline-flex;
}
.section-assignees > span + span {
  margin-left: -7px;
}
.section-assignees :deep(.avatar),
.section-assignees__more {
  width: 25px;
  height: 25px;
  border: 2px solid #e8eeed;
  font-size: 9px;
}
.section-assignees__more {
  z-index: 1;
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: var(--ink-800);
  color: white;
  font-weight: 750;
}
.section-progress {
  grid-column: 5;
  justify-self: end;
  width: min(210px, 25vw);
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 9px;
  font-size: 11px;
}
.section-blocked {
  display: flex;
  grid-column: 6;
  align-items: center;
  gap: 5px;
  color: var(--danger);
  font-size: 11px;
  font-weight: 700;
}
.task-row {
  min-height: 52px;
  align-items: stretch;
  border-bottom: 1px solid #e1e7e5;
  background: white;
  cursor: default;
}
.task-row:hover,
.task-row:focus-within {
  background: #f8fbf9;
}
.task-row:focus-within {
  position: relative;
  z-index: 1;
  box-shadow: inset 3px 0 var(--focus);
}
.task-row--summary {
  background: #f7faf9;
  font-weight: 650;
}
.task-row--milestone {
  background: #fbfdff;
}
.task-row--blocked {
  background: #fffafa;
}
.task-row--internal {
  background: #fbf9fc;
}
.task-row--complete {
  background: var(--success-soft);
}
.task-row--complete:hover,
.task-row--complete:focus-within {
  background: var(--success-soft);
}
.task-row--complete .task-title__copy strong {
  color: var(--success);
}
.task-row--not-applicable {
  background: #f5f6f5;
  color: var(--text-faint);
}
.task-row--not-applicable .task-title__copy strong {
  color: var(--text-faint);
  font-weight: 500;
}
.task-row--not-applicable .task-symbol,
.task-row--not-applicable .owner-cell :deep(.avatar),
.task-row--not-applicable .activity-cell {
  filter: grayscale(1);
  opacity: 0.68;
}
.task-row--not-applicable:hover,
.task-row--not-applicable:focus-within {
  background: #f0f2f1;
}
.task-cell {
  min-width: 0;
  display: flex;
  align-items: center;
  border-right: 1px solid #e4e9e8;
}
.task-cell:last-child {
  border-right: 0;
}
.task-title {
  gap: 8px;
  padding-left: calc(12px + var(--level) * 24px);
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.task-title__copy {
  min-width: 0;
  display: grid;
  gap: 2px;
}
.task-title__copy strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-title__line {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.task-title__line strong {
  min-width: 0;
}
.internal-marker {
  flex: 0 0 auto;
  color: var(--internal);
}
.completion-marker {
  flex: 0 0 auto;
  color: var(--success);
}
.task-title__copy small {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-soft);
  font-weight: 400;
}
.task-title__copy .task-type-label {
  color: var(--ink-800);
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.045em;
  line-height: 1.2;
  text-transform: uppercase;
}
.task-row--milestone .task-type-label {
  color: var(--shared);
}
.task-symbol {
  width: 18px;
  display: grid;
  place-items: center;
  color: #82918e;
}
.task-symbol--milestone {
  color: var(--shared);
}
.task-symbol--summary {
  color: var(--ink-800);
}
.owner-cell {
  gap: 8px;
  background: transparent;
  color: inherit;
  text-align: left;
}
.owner-cell--interactive {
  cursor: pointer;
}
.owner-cell--interactive:hover {
  background: var(--sheet-subtle);
}
.owner-cell :deep(.avatar) {
  width: 26px;
  height: 26px;
  font-size: 9px;
}
.owner-cell > span:last-child {
  display: grid;
}
.owner-cell strong {
  font-size: 12px;
}
.owner-cell small {
  color: var(--text-faint);
  font-size: 10px;
}
.collection-progress-cell {
  gap: 8px;
}
.collection-progress-cell strong {
  min-width: 30px;
  font-size: 11px;
  text-align: right;
}
.collection-progress-track {
  width: 100%;
  height: 6px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--sheet-subtle);
}
.collection-progress-track > span {
  display: block;
  height: 100%;
  background: var(--brand);
}
.calculated-status {
  color: var(--text-soft);
  font-size: 12px;
}
.calculated-status--complete {
  color: var(--success);
  font-weight: 750;
}
.status-select {
  width: 100%;
  min-height: 28px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  font-size: 12px;
  cursor: pointer;
}
.status-select--complete {
  border-color: var(--success);
  background: var(--success-soft);
  color: var(--success);
  font-weight: 750;
}
.status-select:hover,
.status-select:focus {
  border-color: var(--rule-strong);
  background: white;
}
.health {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  white-space: nowrap;
}
.health > span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--success);
}
.health--at-risk > span {
  background: var(--warning);
}
.health--blocked > span,
.health--overdue > span {
  background: var(--danger);
}
.due-risk {
  color: var(--danger);
  font-weight: 700;
}
.activity-cell {
  gap: 8px;
}
.activity-link {
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 28px;
  min-height: 28px;
  justify-content: center;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-soft);
  font-size: 11px;
  cursor: pointer;
}
.activity-link:hover,
.activity-link:focus-visible {
  background: var(--sheet-strong);
  color: var(--ink-900);
}
.row-menu {
  width: 28px;
  height: 28px;
  margin-left: auto;
  opacity: 0.45;
}
.menu-dismiss {
  position: fixed;
  z-index: 179;
  inset: 0;
  width: 100%;
  background: transparent;
}
.task-actions-menu {
  position: fixed;
  z-index: 180;
  width: 250px;
  max-height: calc(100vh - 16px);
  overflow-y: auto;
  display: grid;
  padding: 6px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: white;
  box-shadow: var(--shadow-menu);
}
.task-actions-menu button {
  min-height: 36px;
  padding: 7px 9px;
  border-radius: 4px;
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.owner-menu {
  width: 286px;
}
.owner-menu__title {
  padding: 7px 9px 5px;
  color: var(--ink-900);
  font-size: 12px;
  font-weight: 800;
}
.owner-menu > button {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 15px;
  align-items: center;
  gap: 9px;
}
.owner-menu > button :deep(.avatar) {
  width: 28px;
  height: 28px;
  font-size: 9px;
}
.owner-menu > button > span {
  min-width: 0;
  display: grid;
  gap: 1px;
}
.owner-menu > button strong,
.owner-menu > button small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.owner-menu > button strong {
  font-size: 12px;
}
.owner-menu > button small,
.owner-menu__status {
  color: var(--text-faint);
  font-size: 10px;
}
.owner-menu__status {
  padding: 6px 9px;
}
.collection-menu-group {
  display: grid;
  margin-block: 5px;
  padding-block: 5px;
  border-block: 1px solid var(--rule);
}
.collection-menu-group > p:first-child {
  padding: 4px 9px 3px;
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.collection-menu-group button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.collection-menu-group button span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.collection-menu-group button:disabled {
  cursor: default;
  opacity: 0.62;
}
.collection-menu-group > small,
.collection-menu-empty,
.collection-menu-error {
  padding: 4px 9px 6px;
  color: var(--text-soft);
  font-size: 11px;
  line-height: 1.35;
}
.collection-menu-error {
  color: var(--danger);
}
.task-actions-menu button:not(:disabled):hover,
.task-actions-menu button:focus-visible {
  background: var(--brand-soft);
}
.task-row:hover .row-menu,
.task-row:focus-within .row-menu {
  opacity: 1;
}
.empty-plan {
  min-height: 300px;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 8px;
  color: var(--text-soft);
}
.empty-plan strong {
  color: var(--text);
}
@media (max-width: 700px) {
  .plan-list {
    min-width: 0;
  }
  .section-controls {
    min-height: 48px;
  }
  .section-control {
    min-height: 44px;
  }
  .plan-columns {
    display: none;
  }
  .plan-list--selection .plan-columns {
    display: grid;
    grid-template-columns: 42px 1fr;
  }
  .plan-list--selection .plan-columns::after {
    content: "Select visible tasks";
    padding-inline: 8px;
  }
  .plan-list--selection .plan-columns > span {
    display: none;
  }
  .section-row {
    top: 0;
    grid-template-columns: 20px 1fr auto;
  }
  .section-toggle {
    grid-template-columns: 22px minmax(0, 1fr) auto;
  }
  .section-meta {
    display: none;
  }
  .section-assignees {
    grid-column: 3;
    padding-right: 2px;
  }
  .section-assignees > span:nth-child(n + 5) {
    display: none;
  }
  .section-progress,
  .section-blocked {
    display: none;
  }
  .task-row {
    min-height: 78px;
    grid-template-columns: 1fr auto;
    padding: 9px 10px;
  }
  .plan-list--selection .task-row {
    grid-template-columns: 34px 1fr auto;
  }
  .plan-list--selection .selection-column {
    grid-column: 1;
    grid-row: 1 / 3;
    border-right: 0;
  }
  .plan-list--selection .task-title {
    grid-column: 2 / -1;
  }
  .plan-list--selection .owner-cell {
    grid-column: 2;
  }
  .plan-list--selection .status-cell {
    grid-column: 3;
  }
  .task-cell {
    border: 0;
    padding: 2px 5px;
  }
  .task-title {
    grid-column: 1 / -1;
    padding-left: calc(4px + var(--level) * 15px);
  }
  .owner-cell {
    grid-column: 1;
  }
  .date-cell,
  .health-cell,
  .activity-cell {
    display: none;
  }
  .status-cell {
    justify-self: end;
    grid-column: 2;
    grid-row: 2;
  }
  .row-menu {
    opacity: 1;
  }
}
</style>
